import { drawActorSprite } from '../ActorSprite';
import { ACTOR_CACHE_DRAW_SCALE } from '../ActorSpriteCache';
import { drawProceduralMobType, isProceduralMobType, PROCEDURAL_ENTITY_TYPES } from './registry';
import { getOrCreateSkeletonSpriteSet, nearestSkeletonFacingAngle, SKELETON_DRAW_SCALE } from './SkeletonSpriteRenderer';

const PROCEDURAL_FACING_ANGLES = [0, 60, 90, 120, 180, 240, 270, 300] as const;
const PROCEDURAL_MOB_DRAW_SCALE = 1.408;
const PROCEDURAL_MOB_CACHE_BASE_SIZE = 96;

type ProceduralFacingAngle = (typeof PROCEDURAL_FACING_ANGLES)[number];
type ProceduralSpriteSet = Record<ProceduralFacingAngle, HTMLCanvasElement>;

const mobProceduralSpriteCache = new Map<string, ProceduralSpriteSet>();

function normalizeRotation(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

function shortestAngleDelta(a: number, b: number): number {
  let delta = normalizeRotation(a) - normalizeRotation(b);
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return Math.abs(delta);
}

function nearestProceduralFacingAngle(rotation: number): ProceduralFacingAngle {
  let nearest: ProceduralFacingAngle = PROCEDURAL_FACING_ANGLES[0];
  let best = shortestAngleDelta(rotation, nearest);
  for (let i = 1; i < PROCEDURAL_FACING_ANGLES.length; i += 1) {
    const candidate = PROCEDURAL_FACING_ANGLES[i];
    const dist = shortestAngleDelta(rotation, candidate);
    if (dist < best) {
      best = dist;
      nearest = candidate;
    }
  }
  return nearest;
}

function getOrCreateProceduralMobSpriteSet(entityType: string): ProceduralSpriteSet | null {
  if (!isProceduralMobType(entityType)) {
    return null;
  }

  const existing = mobProceduralSpriteCache.get(entityType);
  if (existing) {
    return existing;
  }

  const devicePixelRatio =
    typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? Math.max(1, window.devicePixelRatio)
      : 1;
  const cacheScale = Math.max(1, Math.ceil(devicePixelRatio));
  const baseSize = PROCEDURAL_MOB_CACHE_BASE_SIZE * cacheScale;
  const cx = baseSize / 2;
  const cy = baseSize / 2;
  const spriteSize = baseSize * 0.9;
  const spriteSet = {} as unknown as ProceduralSpriteSet;

  for (let i = 0; i < PROCEDURAL_FACING_ANGLES.length; i += 1) {
    const angle = PROCEDURAL_FACING_ANGLES[i];
    const canvas = document.createElement('canvas');
    canvas.width = baseSize;
    canvas.height = baseSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.imageSmoothingEnabled = true;
    drawProceduralMobType(entityType, ctx, cx, cy, spriteSize, angle);
    spriteSet[angle as ProceduralFacingAngle] = canvas;
  }

  mobProceduralSpriteCache.set(entityType, spriteSet);
  return spriteSet;
}

export function drawMobVisual(
  ctx: CanvasRenderingContext2D,
  options: {
    entityType: string;
    centerX: number;
    centerY: number;
    size: number;
    rotation: number;
    color: string;
    alpha?: number;
    isMoving: boolean;
  }
): void {
  const { entityType, centerX, centerY, size, rotation, color, isMoving } = options;
  const alpha = options.alpha ?? 1;
  const cachedProceduralSpriteSet = getOrCreateProceduralMobSpriteSet(entityType);
  if (cachedProceduralSpriteSet) {
    const cachedProceduralSprite = cachedProceduralSpriteSet[nearestProceduralFacingAngle(rotation)];
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = true;
    const drawSize = size * PROCEDURAL_MOB_DRAW_SCALE;
    ctx.drawImage(
      cachedProceduralSprite,
      centerX - drawSize / 2,
      centerY - drawSize / 2,
      drawSize,
      drawSize
    );
    ctx.restore();
    return;
  }

  if (entityType.includes('skeleton')) {
    const pose = isMoving ? 'jumping' : 'idle';
    const cachedSkeletonSet = getOrCreateSkeletonSpriteSet(color, undefined, pose); // We can pass accessories JSON here later if mobs get items
    if (cachedSkeletonSet) {
      const cachedSkeletonSprite = cachedSkeletonSet[nearestSkeletonFacingAngle(rotation)];
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.imageSmoothingEnabled = true;
      const drawSize = size * SKELETON_DRAW_SCALE;
      ctx.drawImage(
        cachedSkeletonSprite,
        centerX - drawSize / 2,
        centerY - drawSize / 2,
        drawSize,
        drawSize
      );
      ctx.restore();
      return;
    }
  }

  drawActorSprite(ctx, {
    centerX,
    centerY,
    size,
    rotation,
    color,
    alpha,
  });
}

export function createMobIconElement(entityType: string): HTMLElement | null {
  const size = 28;
  const cachedProceduralSpriteSet = getOrCreateProceduralMobSpriteSet(entityType);
  if (cachedProceduralSpriteSet) {
    const cachedProceduralSprite = cachedProceduralSpriteSet[0];
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(cachedProceduralSprite, 0, 0, size, size);
    }
    return canvas;
  }

  if (entityType.includes('skeleton')) {
    // Basic fallback color for UI icons
    const cachedSkeletonSet = getOrCreateSkeletonSpriteSet('#e2e8f0');
    if (cachedSkeletonSet) {
      const cachedSkeletonSprite = cachedSkeletonSet[0];
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(cachedSkeletonSprite, 0, 0, size, size);
      }
      return canvas;
    }
  }

  return null;
}

export function getMobVisualDrawSize(entityType: string | undefined, size: number): number {
  if (entityType && isProceduralMobType(entityType)) {
    return size * PROCEDURAL_MOB_DRAW_SCALE;
  }
  if (entityType && entityType.includes('skeleton')) {
    return size * SKELETON_DRAW_SCALE;
  }
  return size * ACTOR_CACHE_DRAW_SCALE;
}

function prewarmProceduralMobSpriteCache(): void {
  if (typeof document === 'undefined') {
    return;
  }
  for (const entityType of PROCEDURAL_ENTITY_TYPES) {
    getOrCreateProceduralMobSpriteSet(entityType);
  }
}

prewarmProceduralMobSpriteCache();
